/**
 * Krok UX2 — KOKPIT ZAKÁZKY PRO MAKLÉŘE: TŘI OTÁZKY.
 *
 * Nález R.B. 29. 9. 2026 nad detailem Poděbradova 141/44: „Proč je tu
 * připravenost 0 %? Je to aktuální? Pro člověka je to celé komplikované."
 * Dnešní kokpit je pohled auditora — brány, pásma jistoty, revize — a
 * neříká, co má makléř udělat ani kdy se data naposledy pořídila.
 *
 * Tento modul je ČISTÝ výpočet nad daty, která už detail zná (`CaseDetail`
 * z `lib/dd-api.ts`) a nad dvěma malými seznamy, které si načte obrazovka
 * (lidské úkoly a stav reportu). Nic se nedohaduje a nic se nepočítá znovu:
 *
 *   1. „Je to v pořádku?“ — semafor z UZAVŘENÝCH DAT (kritická rizika =
 *      otevřené otázky s dopadem CRITICAL, zavřený přístup = otázka
 *      `Q-ACCESS-OBSTACLE-*`, rozpory = fakta ve stavu CONFLICT, neověřené
 *      = brány, které nejsou zavřené). Žádný nový výpočet rizik,
 *   2. „Co musím udělat?“ — seznam lidských úkolů, každý s jedním tlačítkem;
 *      hotový je ten, kdo má odpověď (otázka ANSWERED, úkol CONFIRMED/SKIPPED),
 *      nebo ten, kdo už potvrdil rozpor typu,
 *   3. „Co dám klientovi?“ — stav a akce, skládané vektoru.
 *
 * ROZHODNUTÍ UX2-1 (aktuálnost): „Může být zastaralé“ se rozhoduje PODLE
 * POSLEDNÍHO DOKONČENÉHO BĚHU (`research.finishedAt`) a 14 dnů. Druhá
 * půlka zadání („starší než poslední nasazení pipeline se změnou pravidel“)
 * vyžaduje verzi pravidel, kterou dnes API na případu neposílá
 * (`dd_trust_assessments.policy_version` se do detailu nedostává) — je to
 * NÁLEZ pro linku A, viz REPORT-step-UX2.md. Tady se NEHAJEME a neříkáme
 * „aktuální“ větě, kterou data nemají.
 *
 * ROZHODNUTÍ UX2-2 (potvrzení typu): potvrzení rozporu „zadáno jako
 * pozemek, zjištěn dům“ jde stávající cestou `POST …/claims` s `FREE_TEXT`
 * (krok G4, tvrzení makléře). Server to zapíše jako podklad na vstupu
 * auditu, a tak to i kokpit čte — `research.inputs[].note`. Bez této
 * detaily by úkol zůstal navždy otevřený.
 */

import { t } from '@reos/i18n';
import { systemClock } from '@reos/config';
import type { R3FixtureQuestion } from '@reos/duediligence';
// Krok AS3: prefix „na parcelu se fyzicky nedostaneš“ (G2) i pravidlo „zásadní“
// jsou sdilene s kontrolou Asistentky `dd_blocker_open`.
import { ACCESS_OBSTACLE_QUESTION_PREFIX, isBlockerQuestion } from '@reos/today-metrics';
import { brokerText } from './broker-text.js';
import {
  CLAIM_TYPE_CAPTIONS,
  EVIDENCE_KIND_CAPTIONS,
  factValueSummary,
  humanFactCaption,
  humanQuestionText,
} from './case-captions.js';
import {
  CURRENT_TRUST_POLICY_VERSION,
  GATE_SENTENCE_CLAIM_TYPES,
  isSelfFetchableClaim,
  MANDATORY_GATES_BY_CASE_KIND,
  UNIT_FLATS_CONFLICT_FACT_KEY,
} from './gate-catalog.js';
import { reviewedState } from './reviewed-state.js';
import { gateCaseKindOf, type CaseSubject, type GateCaseKind } from './case-subject.js';
import { gateProgress } from './case-gates.js';
import { conflictFacts, openQuestionsSorted, type CaseDetail, type R3FixtureFact } from './dd-api.js';
import type { CaseTabId } from './texts.js';

const cs = { locale: 'cs' } as const;


/** Po kolika dnech je průzkum považován za možná zastaralý (zadání UX2). */
export const STALE_AFTER_DAYS = 14;

/** Dopad v pořadí — stejné pořadí používá semafor i seznam úkolů. */
const IMPACT_RANK: Readonly<Record<string, number>> = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };

export type TrafficLight = 'ok' | 'attention' | 'blocker';
export type IssueSeverity = 'blocker' | 'attention';

/** Jedna věc, která makléře zajímá — s odkazem na detail v záložce Audit. */
export interface CockpitIssue {
  readonly id: string;
  readonly severity: IssueSeverity;
  /** JEDNA lidská věta: co to znamená pro prodej. */
  readonly sentence: string;
  /** Kde je detail (záložka Audit). */
  readonly tab: CaseTabId;
  /** Odkaz dovnitř záložky (posouvá viewport), když je co. */
  readonly anchor: string | null;
  /** Zdroj nálezu — název registru nebo „průzkum“. */
  readonly source: string | null;
  /** Datum nálezu (ISO) — u faktu jeho vlastní, jinak datum běhu. */
  readonly at: string | null;
  readonly testId: string;
  /**
   * Krok UXV4a2 — rozbalení „Proč a co s tím“ (předloha `Zakazka.dc.html`):
   * `explanation` říká, PROČ to vadí, `todo` CO S TÍM. Zdroj a datum už nese
   * `source`/`at`, takže trojice předlohy (vysvětlení · co udělat · zdroj) je
   * celá tady — jedna věc, jeden řádek, žádné druhé místo, kde se skládá.
   */
  readonly explanation: string;
  readonly todo: string;
}

function czDate(iso: string): string {
  return new Date(iso).toLocaleDateString('cs-CZ', { dateStyle: 'medium' });
}

// ---- 1. JE TO V POŘÁDKU? ---------------------------------------------------

export interface CockpitStatus {
  readonly light: TrafficLight;
  readonly headline: string;
  readonly hint: string;
  readonly issues: readonly CockpitIssue[];
}

/**
 * Lidská věta k braně — z katalogu, jinak obecný fallback.
 *
 * Vycet je `GATE_SENTENCE_CLAIM_TYPES` (vsech dvanact bran registru), NE jen
 * povinne brany: `easements`, `liens`, `utility_capacity`,
 * `zoning_applicability`, `realized_price` a `professional_technical` povinne
 * nejsou, ale na kokpitu se objevit mohou a katalog pro ne vetu ma.
 */
const GATE_SENTENCE_KNOWN: ReadonlySet<string> = new Set(GATE_SENTENCE_CLAIM_TYPES);

function gateSentence(claimType: string): string {
  if (!GATE_SENTENCE_KNOWN.has(claimType)) {
    return t('webAdmin.caseCockpit.issue.gateFallback', cs);
  }
  return t(`webAdmin.caseCockpit.issue.gate.${claimType}` as never, cs);
}

/** Kdy je nález „zásadní“: kritická otázka, zavřený přístup nebo rozpor. */
function issueSeverity(question: R3FixtureQuestion | null): IssueSeverity {
  // Krok AS3: pravidlo „zásadní“ zije v `@reos/today-metrics` — tutez funkci
  // vola kontrola Asistentky `dd_blocker_open`.
  return question !== null && isBlockerQuestion(question) ? 'blocker' : 'attention';
}

function questionSentence(question: R3FixtureQuestion): string {
  const why = brokerText(question.whyItMatters, { fallback: '' });
  if (why !== '') return why;
  const text = brokerText(question.text, { fallback: '' });
  if (text !== '') return text;
  return t('webAdmin.caseCockpit.issue.questionFallback', cs);
}

/**
 * Krok UXV4a2 — co přesně průzkum nezjistil. Je to TEXT OTÁZKY; věta nahoře
 * nese její dopad (`whyItMatters`, viz `questionSentence`). Když je otázka
 * prázdná nebo se s dopadem shoduje, vrátí se obecná věta z katalogu — dvakrát
 * totéž pod sebou by rozbalení nijak nevysvětlilo.
 */
function questionExplanation(question: R3FixtureQuestion): string {
  const text = brokerText(question.text, { fallback: '' });
  if (text === '' || text === questionSentence(question)) {
    return t('webAdmin.caseCockpit.issue.whyQuestion', cs);
  }
  return text;
}

/**
 * Krok GEN1: popisek pro kokpit. `factCaption` (sdileny s reporty) vraci pro
 * klic bez popisku SAM KLIC — v PDF ho chrani katalog, makleřka na kokpitu
 * ale nesmi videt `building_condition.flats_count_conflict`.
 *
 * Krok D2: fallback uz NENI jen tady. Tataz vada byla i v zalozce „Co víme",
 * takze pravidlo zije v `case-captions.ts` (`humanFactCaption`) a obe mista
 * ho volaji — dve kopie teze pravdy se vzdy rozejdou.
 */
const brokerFactCaption = humanFactCaption;

/**
 * Krok PIL1 (vada V7) — LIDSKA VETA ROZPORU.
 *
 * `factValueSummary` (sdileny s reporty) vypise poznamku kolektoru tak, jak
 * je — a na kokpitu zakazky Topolove tak stalo „pocet bytu v RUIAN je nizsi
 * nez cislo pozadovane jednotky - RUIAN muze byt neaktualni" (bez diakritiky,
 * snimek `GEN1/2-06`). Poznamka kolektoru je diagnostika pro vyvoj, ne veta
 * pro makléřku.
 *
 * Veta se proto sklada z HODNOT faktu a z katalogu adminu. Sdileny katalog
 * reportu (`@reos/reports/captions.ts`) se NEMENI — jde do klientskeho PDF
 * a je soucasti schvalene zlate sady (`pnpm test:golden`). Je to tentyz
 * postup, jakym krok GEN1 resil `brokerFactCaption` (O2).
 *
 * Katalog je VYCET: klic, ktery tu neni, si nechava dnesni chovani
 * (`factValueSummary`) — nic se nevymysli.
 */
function conflictValueText(fact: R3FixtureFact): string {
  const value = fact.value;
  if (fact.factKey === UNIT_FLATS_CONFLICT_FACT_KEY && value !== null && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const ruian = record['ruianFlatsCount'];
    const unit = record['requestedUnitNumber'];
    if (ruian !== undefined && ruian !== null && unit !== undefined && unit !== null) {
      return t('webAdmin.caseCockpit.issue.conflictFlatsCount', {
        ...cs,
        values: { ruian: String(ruian), unit: String(unit) },
      });
    }
  }
  return factValueSummary(fact);
}

function conflictSentence(fact: R3FixtureFact): string {
  return t('webAdmin.caseCockpit.issue.conflict', {
    ...cs,
    // Sablona konci teckou; hodnota (veta z poznamky) ji casto nese taky.
    values: { caption: brokerFactCaption(fact.factKey), value: conflictValueText(fact).replace(/\.+$/, '') },
  });
}

/**
 * Semafor + seznam věcí. Řazení: zásadní před pozorem, pak podle `id` —
 * aby se seznam mezi dvěma načteními nerozestavěl.
 */
export function cockpitStatus(
  detail: CaseDetail,
  subject: CaseSubject,
  gateKind: GateCaseKind | null = gateCaseKindOf(subject),
): CockpitStatus {
  const runAt = detail.fixture.research.finishedAt ?? detail.fixture.research.startedAt;
  const gates = gateProgress(detail, gateKind);
  const byId = new Map<string, CockpitIssue>();

  const add = (issue: CockpitIssue): void => {
    if (!byId.has(issue.id)) byId.set(issue.id, issue);
  };

  for (const question of openQuestionsSorted(detail)) {
    const blocked = question.questionKey.startsWith(ACCESS_OBSTACLE_QUESTION_PREFIX);
    add({
      id: `question:${question.id}`,
      severity: issueSeverity(question),
      sentence: blocked
        ? t('webAdmin.caseCockpit.issue.accessBlocked', cs)
        : questionSentence(question),
      tab: 'co-nevime',
      anchor: 'case-unknown',
      source: t('webAdmin.caseCockpit.status.sourceRun', cs),
      at: runAt,
      testId: `case-cockpit-issue-question-${question.questionKey}`,
      // Vysvetleni je SAMA OTAZKA pruzkumu („co presne nevime"); veta nahore
      // uz nese jeji dopad (`whyItMatters`). Kdyz je otazka prazdna, zustane
      // obecna veta z katalogu — nic se nedomysli.
      explanation: questionExplanation(question),
      todo: blocked
        ? t('webAdmin.caseCockpit.issue.todoAccess', cs)
        : t('webAdmin.caseCockpit.issue.todoQuestion', cs),
    });
  }

  for (const fact of conflictFacts(detail.facts)) {
    add({
      id: `conflict:${fact.id}`,
      severity: 'blocker',
      sentence: conflictSentence(fact),
      tab: 'konflikty',
      anchor: null,
      source: t('webAdmin.caseCockpit.status.sourceRun', cs),
      at: fact.sourceDate ?? runAt,
      testId: `case-cockpit-issue-conflict-${fact.factKey}`,
      explanation: t('webAdmin.caseCockpit.issue.whyConflict', cs),
      todo: t('webAdmin.caseCockpit.issue.todoConflict', cs),
    });
  }

  // Brány, které nejsou zavřené. Když seznam ze serveru jmenovatele
  // nepokrývá (NÁLEZ 1 kroku UX1), jmenujeme jen ty, které známe.
  for (const gate of gates.namedOpen) {
    const source = gate.closableBy[0];
    add({
      id: `gate:${gate.gateId}`,
      severity: 'attention',
      sentence: gateSentence(gate.claimType),
      tab: 'co-nevime',
      anchor: 'case-checklist',
      source:
        source === undefined
          ? null
          : (EVIDENCE_KIND_CAPTIONS[source] ?? t('webAdmin.caseCockpit.status.sourceRun', cs)),
      at: runAt,
      testId: `case-cockpit-issue-gate-${gate.gateId}`,
      explanation: t('webAdmin.caseCockpit.issue.whyGate', cs),
      // „Dohledá REOS" vs. „doplň ručně" rozhoduje TENTYZ registr, jaky to
      // rika v seznamu oblasti (`case-review-areas.ts` → `isSelfFetchableClaim`).
      todo: isSelfFetchableClaim(gate.claimType, gate.closableBy)
        ? t('webAdmin.caseCockpit.issue.todoGateLookup', cs)
        : t('webAdmin.caseCockpit.issue.todoGateManual', cs),
    });
  }

  const ordered = [...byId.values()].sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === 'blocker' ? -1 : 1;
    return a.id.localeCompare(b.id);
  });

  const blockers = ordered.filter((issue) => issue.severity === 'blocker').length;
  const count = ordered.length;
  const light: TrafficLight = blockers > 0 ? 'blocker' : count > 0 ? 'attention' : 'ok';
  const headline =
    light === 'blocker'
      ? t('webAdmin.caseCockpit.status.blocker', cs)
      : light === 'attention'
        ? t('webAdmin.caseCockpit.status.attention', { ...cs, values: { count: String(count) } })
        : t('webAdmin.caseCockpit.status.ok', cs);
  const hint =
    light === 'blocker'
      ? t('webAdmin.caseCockpit.status.blockerHint', cs)
      : light === 'attention'
        ? t('webAdmin.caseCockpit.status.attentionHint', cs)
        : t('webAdmin.caseCockpit.status.okHint', cs);

  return { light, headline, hint, issues: ordered };
}

// ---- 2. CO MUSÍM UDĚLAT? ---------------------------------------------------

export type TaskImpact = 'high' | 'medium' | 'low';

export type CockpitTaskAction =
  | { readonly kind: 'answer'; readonly questionId: string; readonly questionKey: string }
  | { readonly kind: 'human-task'; readonly taskId: string; readonly tab: CaseTabId; readonly anchor: string }
  /** Otevreni cizi obrazovky (fronta „K doplnění“) — neni to tlacitko v detailu. */
  | { readonly kind: 'link'; readonly to: string }
  /**
   * Krok PIL1 (vada V6): neoverena OBLAST PROVĚŘENÍ. Vede do teze sekce
   * auditu, kam vede akce „Zadat" u oblasti — jeden cil, dve mista.
   */
  | { readonly kind: 'open-audit'; readonly tab: CaseTabId; readonly anchor: string | null }
  | { readonly kind: 'confirm-kind' };

export interface CockpitTask {
  readonly id: string;
  /** Jedna věta — co přesně má makléř udělat. */
  readonly text: string;
  /**
   * Tentýž úkol ve DVOU–ČTYŘECH slovech („Objednat list vlastnictví“).
   *
   * Krok FIX6 bod 5: primární tlačítko hlavičky detailu („Další krok: …“)
   * nese PRVNÍ otevřený úkol. Dokud bralo `text`, u otázky auditu se do něj
   * dostala celá surová otázka („Odpovězte na otázku: Jaká je indikativní
   * cena za celek 2807 m² …“) — předloha `03-detail-zakazky.html` má krátký
   * lidský krok. Dlouhá věta zůstává v seznamu úkolů i v `title` tlačítka,
   * takže se nic neztrácí.
   */
  readonly shortText: string;
  readonly done: boolean;
  readonly impact: TaskImpact;
  readonly action: CockpitTaskAction;
  readonly testId: string;
}

/** Lidský úkol od `dd_human_tasks` (krok K0e) — jen to, co kokpit potřebuje. */
export interface HumanTaskSummary {
  readonly id: string;
  readonly sourceId: string;
  readonly taskKind: string;
  readonly status: string;
  /** Otázka osnovy, kterou úkol řeší (K1 `questionKeys[0]`). */
  readonly questionKey: string | null;
}

const KNOWN_HUMAN_TASK_SOURCES: ReadonlySet<string> = new Set([
  'cuzk.nahlizeni',
  'cuzk.dpn',
  'isir.public',
  'cee.exekuce',
]);

function humanTaskText(sourceId: string): string {
  if (!KNOWN_HUMAN_TASK_SOURCES.has(sourceId)) {
    return t('webAdmin.caseCockpit.tasks.humanTaskFallback', cs);
  }
  return t(`webAdmin.caseCockpit.tasks.humanTask.${sourceId}` as never, cs);
}

/** Krok FIX6 bod 5: tentýž registr ve dvou slovech (viz `CockpitTask.shortText`). */
function humanTaskShortText(sourceId: string): string {
  if (!KNOWN_HUMAN_TASK_SOURCES.has(sourceId)) {
    return t('webAdmin.caseCockpit.tasks.short.humanTaskFallback', cs);
  }
  return t(`webAdmin.caseCockpit.tasks.short.humanTask.${sourceId}` as never, cs);
}

function impactOf(impact: string | null): TaskImpact {
  const rank = IMPACT_RANK[impact ?? ''] ?? 0;
  if (rank >= 4) return 'high';
  if (rank >= 3) return 'medium';
  return 'low';
}

const IMPACT_ORDER: Readonly<Record<TaskImpact, number>> = { high: 0, medium: 1, low: 2 };

/** Text, podle kterého se pozná, že makléř rozpor typu potvrdil. */
export const KIND_CONFIRMATION_NOTE = t('webAdmin.caseCockpit.kindConfirmationNote', cs);

export interface CockpitTaskInput {
  readonly subject: CaseSubject;
  /** Lidské úkoly z `GET …/cases/:id/human-tasks`. */
  readonly humanTasks: readonly HumanTaskSummary[];
  /** Uzavřené fakt-tvrzzení makléře (`research.inputs[].note`). */
  readonly confirmedNotes: readonly string[];
  /**
   * Krok PIL1 (vada V6) — OBLASTI PROVĚŘENÍ. Detail zakázky psal „Co musím
   * udělat? **Teď nemáte žádný otevřený úkol**" a o dva řádky výš „chybí
   * ověřit 3 věci" (snímek `GEN1/2-07`). Seznam se tu NEPOČÍTÁ ZNOVU: jde
   * přesně ten, který obrazovka už vykreslila (`caseReviewAreas`), aby se obě
   * čísla nemohla rozejít. `undefined` = volající oblasti nezná (starší
   * volání, testy jednotlivých větví) a seznam úkolů zůstává beze změny.
   */
  readonly reviewAreas?: readonly ReviewAreaSummary[] | undefined;
}

/** Oblast prověření tak, jak ji úkoly potřebují (`lib/case-review-areas.ts`). */
export interface ReviewAreaSummary {
  readonly claimType: string;
  readonly label: string;
  readonly verified: boolean;
  readonly action:
    | { readonly kind: 'view' | 'open'; readonly tab: CaseTabId; readonly anchor: string | null }
    | { readonly kind: 'answer'; readonly questionId: string }
    | { readonly kind: 'lookup' };
}

/**
 * Seznam úkolů makléře. Řazení: nejdřív blokující, pak důležité, pak
 * kdykoli; v rámci stejné skupiny podle textu, aby se seznam nerozestavěl.
 */
export function cockpitTasks(
  detail: CaseDetail,
  input: CockpitTaskInput,
): readonly CockpitTask[] {
  const tasks: CockpitTask[] = [];
  const questionByKey = new Map(detail.fixture.questions.map((q) => [q.questionKey, q]));

  // 1) Rozpor zadání a dat — jde potvrdit jedním klikem.
  if (input.subject.declaredDiffers) {
    tasks.push({
      id: 'kind-conflict',
      text: t('webAdmin.caseCockpit.tasks.confirmKind', {
        ...cs,
        values: { declared: input.subject.declaredLabel, detected: input.subject.label },
      }),
      shortText: t('webAdmin.caseCockpit.tasks.short.confirmKind', cs),
      done: input.confirmedNotes.includes(KIND_CONFIRMATION_NOTE),
      impact: 'high',
      action: { kind: 'confirm-kind' },
      testId: 'case-cockpit-task-kind-conflict',
    });
  }

  // 2) Otevřené otázky, na které se dá odpovědět.
  for (const question of openQuestionsSorted(detail)) {
    tasks.push({
      id: `question:${question.id}`,
      text: t('webAdmin.caseCockpit.tasks.questionTask', {
        ...cs,
        // Krok UXP3 (P1-5): zaloha je VETA z katalogu, ne klic otazky.
        values: { text: humanQuestionText(question) },
      }),
      shortText: t('webAdmin.caseCockpit.tasks.short.answerQuestion', cs),
      done: question.status === 'ANSWERED',
      impact: impactOf(question.impact),
      action: { kind: 'answer', questionId: question.id, questionKey: question.questionKey },
      testId: `case-cockpit-task-question-${question.questionKey}`,
    });
  }

  // 3) Lidské úkoly z veřejných registrů (K0e). `RESOLVE_UNKNOWN` se
  //    odpovídá ve frontě „K doplnění“, ne v detailu zakázky.
  for (const humanTask of input.humanTasks) {
    const done = humanTask.status === 'CONFIRMED' || humanTask.status === 'SKIPPED';
    if (humanTask.taskKind === 'RESOLVE_UNKNOWN') {
      const question =
        humanTask.questionKey === null ? undefined : questionByKey.get(humanTask.questionKey);
      const text =
        question === undefined
          ? t('webAdmin.caseCockpit.tasks.humanTaskFallback', cs)
          : t('webAdmin.caseCockpit.tasks.questionTask', {
              ...cs,
              values: { text: humanQuestionText(question) },
            });
      tasks.push({
        id: `human:${humanTask.id}`,
        text,
        shortText: t('webAdmin.caseCockpit.tasks.short.queue', cs),
        done,
        impact: 'medium',
        action: { kind: 'link', to: '/k-doplneni' },
        testId: `case-cockpit-task-queue-${humanTask.id}`,
      });
      continue;
    }
    tasks.push({
      id: `human:${humanTask.id}`,
      text: humanTaskText(humanTask.sourceId),
      shortText: humanTaskShortText(humanTask.sourceId),
      done,
      impact: 'high',
      action: { kind: 'human-task', taskId: humanTask.id, tab: 'co-nevime', anchor: 'human-tasks' },
      testId: `case-cockpit-task-human-${humanTask.id}`,
    });
  }

  /*
   * 4) Krok PIL1 (vada V6) — NEOVERENE OBLASTI PROVĚŘENÍ. Oblast, kterou uz
   *    resi otevrena otazka (bod 2) nebo lidsky ukol (bod 3), se nepridava
   *    podruhe: akce `answer` patri otazce a tu uz seznam ma.
   */
  const answeredQuestionIds = new Set(
    tasks
      .map((task) => (task.action.kind === 'answer' ? task.action.questionId : null))
      .filter((id): id is string => id !== null),
  );
  for (const area of input.reviewAreas ?? []) {
    if (area.verified) continue;
    if (area.action.kind === 'answer' && answeredQuestionIds.has(area.action.questionId)) continue;
    tasks.push({
      id: `area:${area.claimType}`,
      text: t('webAdmin.caseCockpit.tasks.reviewArea', { ...cs, values: { area: area.label } }),
      shortText: t('webAdmin.caseCockpit.tasks.short.reviewArea', cs),
      done: false,
      impact: 'medium',
      action:
        area.action.kind === 'answer'
          ? { kind: 'answer', questionId: area.action.questionId, questionKey: area.claimType }
          : {
              kind: 'open-audit',
              tab: area.action.kind === 'lookup' ? 'co-vime' : area.action.tab,
              anchor: area.action.kind === 'lookup' ? null : area.action.anchor,
            },
      testId: `case-cockpit-task-area-${area.claimType}`,
    });
  }

  return tasks.sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    if (IMPACT_ORDER[a.impact] !== IMPACT_ORDER[b.impact]) return IMPACT_ORDER[a.impact] - IMPACT_ORDER[b.impact];
    return a.text.localeCompare(b.text, 'cs');
  });
}

// ---- AKTUÁLNOST -------------------------------------------------------------

export interface CaseFreshness {
  /** Datum posledního DOKONČENÉHO běhu (ISO), null = nikdy nedoběhl. */
  readonly checkedAt: string | null;
  /**
   * Krok PIL1 (vada V6): ke kterému dni údaje platí — datum dokončeného běhu,
   * a když běh datum nemá, nejnovější `sourceDate` ze zjištěných faktů.
   * `null` = nevíme nic (případ bez běhu i bez faktů).
   */
  readonly dataAsOf: string | null;
  /** Běží právě průzkum. */
  readonly running: boolean;
  /** Kolik dní od posledního běhu; null = nevíme. */
  readonly ageDays: number | null;
  /**
   * Běh proběhl podle JINÝCH pravidel, než platí dnes (zadání UX2: „starší
   * než poslední nasazení pipeline se změnou pravidel"). Když běh verzi
   * nenese, je `false` — netvrdíme nic, co data neříkají.
   */
  readonly policyChanged: boolean;
  /** Po 14 dnech, bez běhu, nebo po změně pravidel je údaj podezřelý. */
  readonly stale: boolean;
  /**
   * Krok D2 — SMÍ SE TU VŮBEC ŘÍCT „PROVĚŘENO"? Jediné pravidlo je
   * `reviewedState` (`lib/reviewed-state.ts`): všechny povinné oblasti
   * ověřené. Datum běhu říká, KDY jsme se ptali, ne že je na co se odkázat.
   */
  readonly reviewed: boolean;
  /** Popisek nad hodnotou: `Prověřeno`, nebo `Zdroje čteny`. */
  readonly caption: string;
  /** Co ještě chybí — lidské názvy neuzavřených povinných oblastí. */
  readonly missing: readonly string[];
  /** `Prověřeno 1. 9. 2026` — jedno slovo pro makléře. */
  readonly label: string;
  /**
   * Totéž BEZ slova „Prověřeno“ — `1. 9. 2026`.
   *
   * Krok FIX6 bod 5: pruh aktuálnosti (`CaseFreshnessBar`) kreslí nad hodnotu
   * popisek „PROVĚŘENO“ z TÉHOŽ katalogového klíče, jaký je v `label`, takže
   * na obrazovce stálo „PROVĚŘENO / Prověřeno 1. 9. 2026“ (nález ze živého
   * ověření 2. 10.). Popisek i hodnota zůstávají, slovo se neopakuje.
   */
  readonly value: string;
  readonly testId: string;
}

/**
 * Krok PIL1 (vada V6 generalky GEN1) — NEJNOVEJSI DATUM ZJISTENEHO UDAJE.
 *
 * `dd_facts.source_date` nese den, ke kteremu udaj plati. Kdyz na pripadu
 * fakta JSOU, nesmi obrazovka rikat „Zatím nebylo prověřeno" — to je stav
 * BEHU, ne stav DAT (na preview beh dobehl ve workeru, ktery do detailu
 * `finishedAt` nedoplnil; snimek `GEN1/2-06`).
 */
function factsAsOf(detail: CaseDetail): string | null {
  let newest: string | null = null;
  for (const fact of detail.fixture.facts) {
    const at = fact.sourceDate;
    if (at === null || at === '') continue;
    if (newest === null || at > newest) newest = at;
  }
  return newest;
}

/**
 * Jak dávno průzkum běžel, LIDSKY (krok UXP4b bod 2, nález P2-6).
 *
 * Do UXP4b se stáří skládalo přímo z `freshness.daysAgo`, takže čerstvě
 * doběhlý průzkum hlásil „před 0 dny" — číslo, které makléři nic neříká
 * a česky se tak ani neřekne. Nula dní je „dnes"; zbytek bere tentýž
 * katalogový klíč jako dřív, aby počet dní zůstal jedna věta na jednom místě.
 */
export function freshnessAgeText(ageDays: number | null): string {
  if (ageDays === null || ageDays <= 0) return t('webAdmin.caseCockpit.freshness.today', cs);
  return t('webAdmin.caseCockpit.freshness.daysAgo', { ...cs, values: { count: String(ageDays) } });
}

export function caseFreshness(detail: CaseDetail, now: Date = systemClock.now()): CaseFreshness {
  const { startedAt, finishedAt } = detail.fixture.research;
  const running = finishedAt === null && startedAt !== null;
  /*
   * Krok PIL1 (vada V6): stari se pocita z BEHU, a kdyz beh datum nema,
   * z NEJNOVEJSIHO ZJISTENEHO UDAJE. Bez toho stalo „Zatím nebylo prověřeno
   * · Může být zastaralé" primo vedle vypsanych udaju z registru.
   */
  const asOf = finishedAt ?? (running ? null : factsAsOf(detail));
  const ageDays =
    asOf === null
      ? null
      : Math.max(0, Math.floor((now.getTime() - new Date(asOf).getTime()) / 86_400_000));
  // Prazdna verze = beh ji nenese (starsi data, import). Nelze z toho usoudit,
  // ze se pravidla zmenila — mlcime misto hadani.
  const ranUnder = detail.fixture.trust.policyVersion;
  const policyChanged = ranUnder !== '' && ranUnder !== CURRENT_TRUST_POLICY_VERSION;
  /*
   * Krok UXP4a bod 2 (nalez P2-2): BEZICI beh neni zastaraly. Karta psala
   * „Průzkum právě běží" a VEDLE TOHO štítek „Může být zastaralé" — bezici beh
   * nema `finishedAt`, takze `ageDays` vyslo `null` a to se cetlo jako stare.
   * Cerstvejsi uz to byt nemuze; stari se posoudi, az beh dobehne.
   */
  const stale = running ? false : ageDays === null || ageDays > STALE_AFTER_DAYS || policyChanged;
  const factsDate = finishedAt === null && !running ? factsAsOf(detail) : null;
  /*
   * Krok D2 (GPT audit 7. 10., Q1): slovo „Prověřeno" smí padnout JEN podle
   * jediného pravidla. Neuzavřené povinné oblasti se místo něj jmenují —
   * „Co ještě chybí" je pravdivější než datum s uklidňujícím slovem.
   */
  const review = reviewedState(detail.fixture.trust.decisionReadiness, {
    missing: openMandatoryAreas(detail),
    checkedAt: finishedAt ?? factsDate,
  });
  const value =
    finishedAt !== null
      ? czDate(finishedAt)
      : running
        ? t('webAdmin.caseCockpit.freshness.running', cs)
        : factsDate !== null
          ? // Nerika se „Prověřeno", protoze beh v detailu dokonceny neni;
            // rika se presne to, co data unesou — k jakemu dni udaje plati.
            t('webAdmin.caseCockpit.freshness.fromFacts', { ...cs, values: { date: czDate(factsDate) } })
          : t('webAdmin.caseCockpit.freshness.never', cs);
  const caption = review.reviewed
    ? t('webAdmin.caseCockpit.freshness.label', cs)
    : t('webAdmin.caseCockpit.freshness.labelRead', cs);
  const label = finishedAt !== null ? `${caption} ${value}` : value;
  return {
    checkedAt: finishedAt,
    dataAsOf: finishedAt ?? factsDate,
    running,
    ageDays,
    policyChanged,
    stale,
    reviewed: review.reviewed,
    caption,
    missing: review.missing,
    label,
    value,
    testId: 'case-freshness',
  };
}

/**
 * Krok D2 — LIDSKE NAZVY NEUZAVRENYCH POVINNYCH OBLASTI.
 *
 * Jmenovatel je `MANDATORY_GATES_BY_CASE_KIND` (tataz tabulka jako na
 * serveru, lokalni kopie v `gate-catalog.ts`); uzavrene se poznaji ze
 * seznamu bran, ktery poslal server. Branu, kterou seznam nezna, nelze
 * prohlasit za uzavrenou — zustava mezi chybejicimi.
 */
function openMandatoryAreas(detail: CaseDetail): readonly string[] {
  const kind = detail.fixture.trust.caseKind;
  const mandatory =
    kind === 'LAND' || kind === 'HOUSE' || kind === 'FLAT'
      ? MANDATORY_GATES_BY_CASE_KIND[kind]
      : [];
  const closed = new Set(
    detail.fixture.gates.filter((gate) => gate.state === 'CLOSED').map((gate) => gate.claimType),
  );
  return mandatory
    .filter((claimType) => !closed.has(claimType))
    .map((claimType) => CLAIM_TYPE_CAPTIONS[claimType] ?? claimType);
}

// ---- 3. CO DÁM KLIENTOVI? ---------------------------------------------------

/**
 * Stav VYROBY klientského PDF (`dd_exports`), ne obchodní objednávky.
 * `none` = export si nikdo nevyžádal.
 */
export type ClientReportExportStatus = 'none' | 'QUEUED' | 'RUNNING' | 'DONE' | 'FAILED';

export interface ClientReportInput {
  /** Je k dispozici hotové PDF ke stažení. */
  readonly pdfReady: boolean;
  /** Nejnovější uvolněný report: kdy a komu. */
  readonly released: { readonly at: string; readonly email: string } | null;
  /** Report v obchodní frontě, který ještě uvolněný nebyl. */
  readonly pendingOrderId: string | null;
  /** Body z bodu 2, které v reportu chybí (budou „Doplňuje specialista“). */
  readonly missing: readonly string[];
  /**
   * Krok TOKYFIX1 (překážka P3/5): STAV VÝROBY PDF.
   *
   * Měření TOKY1 (14. 10. 2026) čekalo deset minut u karty, která celou dobu
   * hlásila „K této zakázce zatím není žádná objednávka reportu.“ —
   * tedy odpověď na úplně jinou otázku (obchodní objednávka z veřejného
   * prodeje) než „kde je moje PDF“. Běžná zakázka kanceláře objednávku
   * nemá a mít nemusí: klientský report se vyrábí z doběhlého prověření
   * stejně jako podrobný.
   *
   * Nedodané pole (`undefined`) znamená „stav výroby neznám“ — pak funkce
   * drží opatrnější větu kroku UX2 a o výrobě netvrdí nic.
   */
  readonly exportStatus?: ClientReportExportStatus | undefined;
  /** Proč výroba selhala (`dd_exports.failure_reason`), když selhala. */
  readonly exportError?: string | null | undefined;
}

export interface ClientReportState {
  readonly headline: string;
  readonly approveDisabled: boolean;
  readonly missing: readonly string[];
}

export function clientReportState(input: ClientReportInput): ClientReportState {
  const headline = ((): string => {
    // Odeslano klientovi vyhrava nad vsim — tam uz neni co resit.
    if (input.released !== null) {
      return t('webAdmin.caseCockpit.report.statusSent', {
        ...cs,
        values: { date: czDate(input.released.at), email: input.released.email },
      });
    }
    /*
     * Volajici, ktery stav vyroby NEZNA (`exportStatus` nedodan), zustava
     * u vety kroku UX2: bez polozky ve fronte uvolneni nepozname, zda report
     * nikdy neobjednal, nebo uz odesel (ROZHODNUTI UX2-4). Rikat mu
     * „ještě není vyrobený" by bylo tvrzeni bez podkladu.
     */
    if (input.exportStatus === undefined) {
      return input.pendingOrderId !== null
        ? t('webAdmin.caseCockpit.report.statusPending', cs)
        : t('webAdmin.caseCockpit.report.noOrder', cs);
    }
    const exportStatus: ClientReportExportStatus = input.exportStatus;
    /*
     * Krok TOKYFIX1 (prekazka P3/5): nejdriv VYROBA, pak obchod. Vyroba je
     * to, co makler(ka) na karte prave dela; objednavka z verejneho prodeje
     * reportu je az otazka odeslani.
     */
    if (exportStatus === 'FAILED') {
      return t('webAdmin.caseCockpit.report.statusFailed', {
        ...cs,
        values: { error: input.exportError ?? '' },
      });
    }
    if (exportStatus === 'QUEUED' || exportStatus === 'RUNNING') {
      return t('webAdmin.caseCockpit.report.statusBuilding', cs);
    }
    if (input.pendingOrderId !== null) {
      return exportStatus === 'DONE'
        ? t('webAdmin.caseCockpit.report.statusPending', cs)
        : t('webAdmin.caseCockpit.report.statusNotReady', cs);
    }
    return exportStatus === 'DONE'
      ? t('webAdmin.caseCockpit.report.statusReady', cs)
      : t('webAdmin.caseCockpit.report.statusNotMade', cs);
  })();
  return {
    headline,
    // Uvolnit se da jen objednavku, ktera existuje a report ma hotovy.
    approveDisabled: input.pendingOrderId === null || !input.pdfReady,
    missing: input.missing,
  };
}
